import { TypedEventEmitter } from './event-emitter.js';
import { CC_NAMES, MIDI } from './constants.js';
import type {
  OddballParserEventMap,
  OddballEvent,
  TapEvent,
  ShakeEvent,
  TwistEvent,
  NoteOffEvent,
  CCUpdateEvent,
  UnknownMidiEvent,
} from './types.js';
import { GestureNote } from './types.js';

export type ParserInput =
  | Uint8Array
  | [number, number, number]
  | { data: Uint8Array };

export class OddballParser extends TypedEventEmitter<OddballParserEventMap> {
  private timestampFn: () => number;

  constructor(options?: { timestampFn?: () => number }) {
    super();
    this.timestampFn = options?.timestampFn ?? (() => performance.now());
  }

  feed(input: ParserInput): OddballEvent {
    let bytes: Uint8Array | [number, number, number];

    if (input instanceof Uint8Array) {
      bytes = input;
    } else if (Array.isArray(input)) {
      bytes = input;
    } else {
      bytes = input.data;
    }

    const status = bytes[0];
    const data1 = bytes[1] ?? 0;
    const data2 = bytes[2] ?? 0;
    const cmd = status & MIDI.COMMAND_MASK;
    const timestamp = this.timestampFn();

    let event: OddballEvent;

    if (cmd === MIDI.NOTE_ON && data2 > 0) {
      event = this.parseNoteOn(data1, data2, timestamp);
    } else if (cmd === MIDI.NOTE_OFF || (cmd === MIDI.NOTE_ON && data2 === 0)) {
      event = { type: 'noteOff', note: data1, timestamp } satisfies NoteOffEvent;
    } else if (cmd === MIDI.CC) {
      event = {
        type: 'cc',
        cc: data1,
        name: CC_NAMES[data1] ?? `CC${data1}`,
        value: data2,
        timestamp,
      } satisfies CCUpdateEvent;
    } else {
      event = { type: 'unknown', status, data1, data2, timestamp } satisfies UnknownMidiEvent;
    }

    this.emit('event', event);
    this.emit(event.type as keyof OddballParserEventMap, event as any);
    return event;
  }

  private parseNoteOn(note: number, velocity: number, timestamp: number): TapEvent | ShakeEvent | TwistEvent {
    switch (note) {
      case GestureNote.Tap:
        return { type: 'tap', velocity, timestamp };
      case GestureNote.Shake:
        return { type: 'shake', velocity, timestamp };
      case GestureNote.Twist:
        return { type: 'twist', velocity, timestamp };
      default:
        return { type: 'tap', velocity, timestamp };
    }
  }
}
